import orderBy from 'lodash/orderBy';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';

import { getValidObject } from '../../utils/objectManipulation';
import { getBrandToProductIdMap, getSortedProducts } from '../../utils/products';
import NoProductsPlaceholder from '../NoProductsPlaceholder';

import ProductListBanner from './ProductListBanner';
import ProductListItem from './ProductListItem';
import styles from './ProductMenu.module.scss';
import ThirdLevelCategories from './ThirdLevelCategories';

const ALL_BRANDS_VALUE = '';

export default function ProductList({
  products = [],
  category = {},
  slug,
  banners = [],
  categories,
  showBySelection,
  isSecondLevelPage,
  loading,
  categoriesShowing = [],
  itemListId,
  itemListName,
}) {
  const [productsShown, setProductsShown] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(ALL_BRANDS_VALUE);
  const [brandProducts, setBrandProducts] = useState([]);
  const [selectedThirdLevelCategoryId, setSelectedThirdLevelCategoryId] = useState('0');
  const [brandToProductIdsMap, setBrandToProductIdsMap] = useState({});
  const router = useRouter();

  const productsParentCategory = products[0]?.category?.categoryTree?.parent || products[0]?.category?._id;
  const offerTitleObject = categoriesShowing.find(_category => _category._id === productsParentCategory);
  const offerTitle = offerTitleObject?.name;

  function getThirdLevelFromSlugAscii(parentCategory, slugAscii) {
    return parentCategory.subCategories.find(thirdLevel => thirdLevel.slugAscii === slugAscii);
  }

  function addParamsOnURL(filterId = selectedThirdLevelCategoryId, brand = selectedBrand) {
    if (filterId === '0') {
      const query = {
        ...getValidObject('brand', brand),
      };
      router.push(
        {
          pathname: `/categories/${slug.join('/')}`,
          query,
        },
        undefined,
        { shallow: true }
      );
      setSelectedThirdLevelCategoryId('0');
      return;
    }
    const filter = category.subCategories.find(thirdLevelCategory => thirdLevelCategory._id === filterId);
    const query = {
      ...getValidObject('filter', filter?.slugAscii),
      ...getValidObject('brand', brand),
    };
    router.push(
      {
        pathname: `/categories/${slug.join('/')}`,
        query,
      },
      undefined,
      { shallow: true }
    );
  }
  const [thirdLvlCategoriesSorted, setThirdLevelCategoriesSorted] = useState([]);

  function displayThirdLevel(categorySelected) {
    const { subCategories } = categorySelected;
    return subCategories.some(c => c.numberOfProductsToDisplay !== 0);
  }

  function onSelectBrand({ target: { value } }) {
    setSelectedBrand(value);
    addParamsOnURL(selectedThirdLevelCategoryId, value);
  }

  function onClickThirdLevelCategory(categoryId, showEverything) {
    if (categoryId === selectedThirdLevelCategoryId) {
      return;
    }
    if (showEverything) {
      setSelectedBrand(ALL_BRANDS_VALUE);
      addParamsOnURL('0', null);
      setSelectedThirdLevelCategoryId('0');
      return;
    }
    setSelectedBrand(ALL_BRANDS_VALUE);
    addParamsOnURL(categoryId, null);
    setSelectedThirdLevelCategoryId(categoryId);
  }

  function getProductsByCategory() {
    if (selectedThirdLevelCategoryId === '0') {
      const productsByCategory = products;
      return productsByCategory;
    }
    const productsByCategory = !selectedThirdLevelCategoryId
      ? products
      : products.filter(p => p.category._id === selectedThirdLevelCategoryId);
    return productsByCategory;
  }

  function getProductsByBrand(productsByCategory) {
    const productsByBrand =
      selectedBrand === ALL_BRANDS_VALUE
        ? productsByCategory
        : productsByCategory.filter(({ _id }) => brandToProductIdsMap[selectedBrand].includes(_id));
    return productsByBrand;
  }

  function setFilterFromURLParams(brandToProductIdMap) {
    if (router?.query?.filter) {
      const selectedThirdLevel = getThirdLevelFromSlugAscii(category, router.query.filter);
      setSelectedThirdLevelCategoryId(selectedThirdLevel._id);
    }
    if (router?.query?.brand && brandToProductIdMap[router.query.brand]) {
      setSelectedBrand(router?.query?.brand);
    } else if (!router?.query?.brand) {
      setSelectedBrand('');
    }
  }

  function getFilteredProducts() {
    setProductsShown([]);
    const productsByCategory = getProductsByCategory();
    setBrandProducts(productsByCategory);
    const productsByBrand = getProductsByBrand(productsByCategory);
    const productsByBannerOptions = getSortedProducts(productsByBrand, showBySelection);
    setProductsShown(productsByBannerOptions);
  }

  useEffect(() => {
    if (selectedThirdLevelCategoryId) setSelectedThirdLevelCategoryId('0');
  }, [slug]);

  useEffect(() => {
    if (!displayThirdLevel(category)) return;
    const { subCategories: thirdLevelCategories } = category;
    const sortedCategories = orderBy(thirdLevelCategories, ['categoryIndex', 'divisionCode'], 'asc');
    setThirdLevelCategoriesSorted(sortedCategories);
  }, []);

  useEffect(() => {
    getFilteredProducts();
  }, [selectedBrand, selectedThirdLevelCategoryId, showBySelection, products]);

  useEffect(() => {
    if (slug.length === 2) {
      const brandToProductIdMap = getBrandToProductIdMap(brandProducts);
      setBrandToProductIdsMap(brandToProductIdMap);
      setFilterFromURLParams(brandToProductIdMap);
    }
  }, [brandProducts]);

  if (!isSecondLevelPage && productsShown.length === 0) return null;
  const brandNames = Object.keys(brandToProductIdsMap).sort();

  return (
    <div className={styles.categoryContainer}>
      {((productsShown.length !== 0 && slug[0] === 'offers') || slug[0] !== 'offers') && (
        <div data-cy="list-of-products">
          <div className={styles.listHeader}>
            <h1 className={styles.listTitle}>{slug[0] === 'offers' ? offerTitle : category.name}</h1>
            {slug.length === 2 && brandNames && (
              <div className={styles.options}>
                <span className={styles.hideOnSmallScreen}>Μάρκα: </span>
                <select className={styles.selectBrand} onChange={onSelectBrand} value={selectedBrand}>
                  <option value={ALL_BRANDS_VALUE}>Όλα</option>
                  {brandNames.map(b => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {slug.length === 2 && displayThirdLevel(category) && (
            <ThirdLevelCategories
              categories={thirdLvlCategoriesSorted}
              selectedCategoryId={selectedThirdLevelCategoryId}
              onClick={onClickThirdLevelCategory}
            />
          )}
          <div className={styles.bannerContainer}>
            <ProductListBanner
              slug={slug}
              banners={banners}
              categories={categories}
              category={category}
              isSecondLevelPage={isSecondLevelPage}
            />
            {productsShown.length !== 0 && !loading && (
              <>
                {productsShown.map((p, i) => (
                  <ProductListItem
                    _product={p}
                    key={p._id}
                    positionInList={i + 1}
                    itemListId={itemListId}
                    itemListName={itemListName}
                  />
                ))}
              </>
            )}
            {productsShown.length === 0 && !loading && (
              <div className={styles.centerNoProducts}>
                <NoProductsPlaceholder />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
